/**
 * Deterministic device fixtures.
 *
 * Stand-in data for the read-only console pages until the live façade is wired
 * (see `web/README.md`). Every record is realistic for an Iranian FTTX fleet:
 * the vendors/models of plan §1 (Huawei, ZTE, C-Data, Fiberhome, VSOL), opaque
 * `nas:`/`sub:`/`policy:` refs and the device id form `acs-device:<serial>`.
 *
 * **The fleet is two-rooted, because the real one is.** The Iranian installed
 * base answers `InternetGatewayDevice.` (TR-098); only the newer minority
 * answers `Device.` (TR-181) — `docs/GAP_ANALYSIS_FA.md` §۳ and
 * `docs/ACS_MULTI_VENDOR_FA.md` §۲.۱. Every path below is *declared* under the
 * root that spells it, never derived from the other by a prefix swap, the way
 * `provisioning/slots.py` declares each slot per root: TR-098 names the OUI
 * leaf `DeviceInfo.ManufacturerOUI` (TR-181: `DeviceInfo.OUI`), keeps `Channel`
 * on `WLANConfiguration.{n}` (TR-181: on `WiFi.Radio.{i}`), and puts PPPoE
 * creds on `WANDevice.1.WANConnectionDevice.{d}.WANPPPConnection.{i}.Username`
 * where TR-181 puts them on `Device.PPP.Interface.{i}.Username` (survey `03`
 * §۱.۳). Copying one root's shape into the other is the bug this layout exists
 * to make impossible.
 *
 * **No invented paths.** Gap `G-PRV-01` deleted the six invented vendor-subtree rows this file used
 * to publish as "virtual params" (`docs/ACS_MULTI_VENDOR_FA.md` §۲.۲ is the order): no ONT on earth
 * has them, and an operator reads the snapshot table as fact — a made-up leaf is worse than an
 * empty table. What they pretended to carry moved to a real leaf where one exists, and left the
 * parameter surface where none does: optical power is an OLT-side reading (see `rxDbm`/`txDbm`
 * below) and a service state is a BSS fact of `nas_ref`, shown by `IdentityPanel`. The scan that
 * keeps them out is `test/noInventedPaths.test.ts`.
 *
 * Determinism matters: the PRNG is seeded, so counts and paging are stable for
 * the tests. Times are relative to module load so "۳ دقیقه پیش" stays believable
 * in a demo — the *values* are ISO-8601 UTC exactly like the wire format.
 */

import type { DeviceSummary, ParameterItem } from '../api/types';

/** The two data-model roots the console speaks (`provisioning/roots.py::TR098/TR181`). */
export type DataModelRoot = 'tr098' | 'tr181';

/** The prefixes the roots are literally spelled with (`provisioning/roots.py::ROOT_PREFIX`). */
export const ROOT_PREFIX: Record<DataModelRoot, string> = {
  tr098: 'InternetGatewayDevice.',
  tr181: 'Device.',
};

/**
 * Which root a name belongs to, read off the path itself.
 *
 * Exact first-segment match — never a brand guess, never a substring test:
 * `DeviceID.Manufacturer` is GenieACS's Inform-derived identity object, not a
 * TR-181 subtree (`provisioning/roots.py::root_of`). Rule M0-2 says the root is
 * read from the device rather than guessed from the vendor, and rule M0-1 says
 * an un-rooted name is refused; an answer of `null` here is that refusal.
 */
export function rootOfPath(path: string): DataModelRoot | null {
  const segment = path.split('.')[0] ?? '';
  if (segment === 'InternetGatewayDevice') return 'tr098';
  if (segment === 'Device') return 'tr181';
  return null;
}

/** One declared leaf of a fixture snapshot: a path, its CWMP type, its writability. */
interface ParamSpec {
  readonly path: string;
  readonly type: string;
  readonly writable: boolean;
}

/** Enriched fixture record: contract fields + what the generators need. */
export interface FixtureDevice extends DeviceSummary {
  /** TR-143 etc. — drives whether speedtest is offered (contract rule). */
  capabilities: string[];
  pppoeUser: string;
  wifiSsid: string;
  wifiBand: '2.4g' | '5g';
  /**
   * Optical readings in **float dBm** — the unit the shipped contract carries at
   * the façade boundary (`ACS_MULTI_VENDOR_FA.md` §۹ rule O-3, `ONT_PARAMS_FA.md`
   * §۵.۲ constraint 3). They are deliberately not parameter paths: for this fleet
   * optical power is an **OLT-side** read (rule O-1; `VENDOR_MATRIX_FA.md` column
   * C9 is `none-use-olt-snmp`), served through `fixtures/olts.ts` and rendered by
   * `formatDbm()`. TR-098 has no standard optical leaf and no witnessed vendor one.
   * `null` is the adapter's own answer when the SNMP read comes back empty.
   */
  rxDbm: number | null;
  txDbm: number | null;
  wanIp: string;
  lanIp: string;
}

const BOOT = Date.now();

/** ISO-8601 UTC, `minutes` in the past (negative = future, unused in fixtures). */
export function isoAgo(minutes: number): string {
  return new Date(BOOT - minutes * 60_000).toISOString();
}

export function minutesAgo(minutes: number): number {
  return BOOT - minutes * 60_000;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd = mulberry32(0xa5c0fe);

/** Per-device stream so a snapshot renders identically on every read. */
function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h = Math.imul(h ^ value.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

type Rand = () => number;

function ri(r: Rand, min: number, max: number): number {
  return min + Math.floor(r() * (max - min + 1));
}

function rpick<T>(r: Rand, items: readonly T[]): T {
  const value = items[Math.floor(r() * items.length) % items.length];
  if (value === undefined) throw new Error('fixture pick on empty list');
  return value;
}

function pick<T>(items: readonly T[]): T {
  const value = items[Math.floor(rnd() * items.length) % items.length];
  if (value === undefined) throw new Error('fixture pick on empty list');
  return value;
}

function intBetween(min: number, max: number): number {
  return min + Math.floor(rnd() * (max - min + 1));
}

interface VendorProfile {
  manufacturer: string;
  oui: string;
  productClass: string;
  model: string;
  serialPrefix: string;
  softwarePrefix: string;
  capabilities: string[];
  ssidSuffix: string;
}

const VENDORS: readonly VendorProfile[] = [
  {
    manufacturer: 'Huawei',
    oui: '48575443',
    productClass: 'HS8546X6',
    model: 'HS8546X6',
    serialPrefix: 'HWTC',
    softwarePrefix: 'V3R020C10',
    capabilities: ['tr069', 'wifi_stats'],
    ssidSuffix: 'FTTH',
  },
  {
    manufacturer: 'ZTE',
    oui: '46494252',
    productClass: 'F6600V9',
    model: 'F6600V9',
    serialPrefix: 'ZTEG',
    softwarePrefix: 'V5.0.7P1',
    capabilities: ['tr069', 'wifi_stats', 'tr143'],
    ssidSuffix: 'TSC',
  },
  {
    manufacturer: 'C-Data',
    oui: '43444154',
    productClass: 'GPT-741X',
    model: 'GPT-741X-D01',
    serialPrefix: 'CDAT',
    softwarePrefix: '3.5.2',
    capabilities: ['tr069'],
    ssidSuffix: 'Cdata',
  },
  {
    manufacturer: 'Fiberhome',
    oui: '46484E44',
    productClass: 'AN5551-04-B',
    model: 'AN5551-04-B',
    serialPrefix: 'FHA',
    softwarePrefix: 'R5.0.2P3',
    capabilities: ['tr069', 'wifi_stats'],
    ssidSuffix: 'FH',
  },
  {
    manufacturer: 'VSOL',
    oui: '56534F4C',
    productClass: 'V1500GP-93128',
    model: 'V1500GP',
    serialPrefix: 'VSL',
    softwarePrefix: '3.3.2-240311',
    capabilities: ['tr069', 'tr143'],
    ssidSuffix: 'VSOL',
  },
];

/**
 * The deliberately "interesting" devices first — each one exists so a specific
 * UI path can be demonstrated without a live fleet:
 *  offline + command rejected, stale snapshot, never-informed (awaiting ZTP),
 *  degraded optical (the OLT-side reading, not a device leaf), unbound (no
 *  `nas_ref`, binding version 0), tr143-capable (speedtest allowed).
 */
interface Story {
  vendor: number;
  online: boolean;
  /** minutes since last inform; null = never informed */
  informAgo: number | null;
  bound: boolean;
  bindingVersion?: number;
  rxDbm?: number | null;
  txDbm?: number | null;
  capabilities?: string[];
}

const STORIES: readonly Story[] = [
  { vendor: 0, online: true, informAgo: 2, bound: true, rxDbm: -19.4, txDbm: 2.9 },
  {
    vendor: 1,
    online: true,
    informAgo: 4,
    bound: true,
    rxDbm: -22.1,
    txDbm: 3.1,
    capabilities: ['tr069', 'wifi_stats', 'tr143'],
  },
  { vendor: 4, online: false, informAgo: 96, bound: true, rxDbm: null, txDbm: null },
  { vendor: 2, online: false, informAgo: 1_440, bound: true },
  { vendor: 3, online: true, informAgo: 7, bound: false },
  { vendor: 0, online: true, informAgo: 1, bound: true, rxDbm: -27.8, txDbm: 2.2 },
  {
    vendor: 1,
    online: true,
    informAgo: 12,
    bound: true,
    bindingVersion: 3,
    rxDbm: -21.2,
    txDbm: 3.4,
  },
  {
    vendor: 4,
    online: true,
    informAgo: 3,
    bound: true,
    rxDbm: -18.9,
    txDbm: 3.6,
    capabilities: ['tr069', 'tr143'],
  },
  { vendor: 2, online: false, informAgo: null, bound: false },
];

const CITY_SUFFIX = [
  'Tehran-01',
  'Tehran-02',
  'Karaj-01',
  'Qom-01',
  'Isfahan-02',
  'Mashhad-01',
  'Tabriz-01',
  'Shiraz-03',
];

function buildDevices(total: number): FixtureDevice[] {
  const out: FixtureDevice[] = [];
  const used = new Set<string>();
  for (let i = 0; i < total; i += 1) {
    const story = i < STORIES.length ? STORIES[i] : undefined;
    const vendor = VENDORS[story ? story.vendor : i % VENDORS.length];
    if (!vendor) continue;

    let serial = '';
    do {
      serial = `${vendor.serialPrefix}${intBetween(100000, 999999)}${pick(['A', 'B', 'C', 'X', 'Y', ''])}`;
    } while (used.has(serial));
    used.add(serial);

    const online = story ? story.online : i % 7 !== 0;
    const informAgo = story ? story.informAgo : online ? intBetween(1, 45) : intBetween(120, 4_000);
    const bound = story ? story.bound : i % 9 !== 0;
    const stale = !online || informAgo === null || informAgo > 240;
    const city = CITY_SUFFIX[i % CITY_SUFFIX.length] ?? 'Tehran-01';

    out.push({
      acs_device_id: `acs-device:${serial}`,
      nas_ref: bound ? `nas:${city.toLowerCase()}-${String(i + 1).padStart(4, '0')}` : null,
      serial,
      oui: vendor.oui,
      product_class: vendor.productClass,
      manufacturer: vendor.manufacturer,
      model: vendor.model,
      software_version: `${vendor.softwarePrefix}.${intBetween(100, 999)}`,
      online,
      last_inform_at: informAgo === null ? null : isoAgo(informAgo),
      binding_version: bound ? (story?.bindingVersion ?? intBetween(1, 2)) : 0,
      stale,
      capabilities: story?.capabilities ?? vendor.capabilities,
      pppoeUser: bound ? `sub${String(i + 1).padStart(4, '0')}@ftth` : '',
      wifiSsid: `${vendor.ssidSuffix}_${serial.slice(-4)}`,
      wifiBand: i % 2 === 0 ? '5g' : '2.4g',
      rxDbm: story?.rxDbm ?? (online ? Number((-18 - rnd() * 9).toFixed(1)) : null),
      txDbm: story?.txDbm ?? (online ? Number((2 + rnd() * 2).toFixed(1)) : null),
      wanIp: `10.${intBetween(8, 60)}.${intBetween(0, 255)}.${intBetween(2, 250)}`,
      lanIp: '192.168.1.1',
    });
  }
  return out;
}

/** Contract order for `GET /devices`: last_inform_at desc, acs_device_id asc. */
export function sortDevices(rows: readonly FixtureDevice[]): FixtureDevice[] {
  return [...rows].sort((a, b) => {
    const at = a.last_inform_at ?? '';
    const bt = b.last_inform_at ?? '';
    if (at !== bt) return at < bt ? 1 : -1;
    return a.acs_device_id < b.acs_device_id ? -1 : 1;
  });
}

/**
 * The TR-098 device of the fleet: a ZTE `F670v8`-style ONT, the shape the Iranian installed base
 * actually is (`ACS_MULTI_VENDOR_FA.md` §۲.۱). It is hand-authored rather than generated because
 * its tree is *declared* above: a generated record would have to take the TR-181 table and swap a
 * prefix into it, which is the move `provisioning/slots.py` forbids and the one this file used to
 * get wrong (its parameter table was named `TR098_COMMON` while carrying only `Device.` paths).
 *
 * `ZTEG` is the vendor id `provisioning/vendor_registry.py:377` maps to ZTE, so the serial reads as
 * the brand it is, and the firmware string follows the version ZTE publishes for this board
 * (survey `03` §۳.۳ records a real `F670L V9.0.10P1N12A` readback). Offline with an old Inform is
 * deliberate and keeps the generator's invariants: the fixture never calls a device online that has
 * not informed in days, and the panel then honestly answers `stale = true` (§۹ of the console
 * standard treats staleness as a state, not an error).
 */
const TR098_F670V8: FixtureDevice = {
  acs_device_id: 'acs-device:ZTEG4471092',
  nas_ref: 'nas:tehran-01-0047',
  serial: 'ZTEG4471092',
  oui: '46494252',
  product_class: 'F670v8',
  manufacturer: 'ZTE',
  model: 'F670v8',
  software_version: 'V9.0.10P1N12A.412',
  online: false,
  // Older than any generated record (≤ 4_000 min), so it joins the sorted fleet without
  // renumbering the devices the OLT placement and the event seeds are keyed to.
  last_inform_at: isoAgo(5_040),
  binding_version: 1,
  stale: true,
  capabilities: ['tr069', 'wifi_stats'],
  pppoeUser: 'sub0047@ftth',
  wifiSsid: 'TSC_1092',
  wifiBand: '2.4g',
  // No optical reading: an offline unit has no fresh SNMP sample, and the reading is an OLT-side
  // value anyway — never a parameter row (rule O-1).
  rxDbm: null,
  txDbm: null,
  wanIp: '10.34.19.108',
  lanIp: '192.168.1.1',
};

export const FIXTURE_DEVICES: readonly FixtureDevice[] = sortDevices([
  ...buildDevices(46),
  TR098_F670V8,
]);

/** Project a fixture record down to the contract shape (no extra fields leak). */
export function toDeviceSummary(device: FixtureDevice): DeviceSummary {
  return {
    acs_device_id: device.acs_device_id,
    nas_ref: device.nas_ref,
    serial: device.serial,
    oui: device.oui,
    product_class: device.product_class,
    manufacturer: device.manufacturer,
    model: device.model,
    software_version: device.software_version,
    online: device.online,
    last_inform_at: device.last_inform_at,
    binding_version: device.binding_version,
    stale: device.stale,
  };
}

export function findDevice(acsDeviceId: string): FixtureDevice | undefined {
  return FIXTURE_DEVICES.find((d) => d.acs_device_id === acsDeviceId);
}

/** Masking secrets client-side: a console must never render a Wi-Fi PSK. */
export const MASKED = '••••••••••';

/**
 * TR-181 (`Device.`) leaves. This table used to be called `TR098_COMMON` while
 * every path in it started with `Device.` — the name was the same accident that
 * produced the six invented rows this file no longer carries (gap `G-PRV-01`),
 * and the fleet now genuinely has two roots, so the tables are named for the
 * root they declare.
 */
const TR181_COMMON: readonly ParamSpec[] = [
  { path: 'Device.DeviceInfo.Manufacturer', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.OUI', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.ProductClass', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.SerialNumber', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.SoftwareVersion', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.HardwareVersion', type: 'string', writable: false },
  { path: 'Device.DeviceInfo.UpTime', type: 'unsignedInt', writable: false },
  { path: 'Device.DeviceInfo.FirstUseDate', type: 'dateTime', writable: false },
  { path: 'Device.DeviceInfo.ProcessStatus.Status', type: 'string', writable: false },
  { path: 'Device.Time.Status', type: 'string', writable: false },
  { path: 'Device.Time.NTPServer1', type: 'string', writable: true },
  { path: 'Device.ManagementServer.URL', type: 'string', writable: true },
  { path: 'Device.ManagementServer.PeriodicInformInterval', type: 'unsignedInt', writable: true },
  { path: 'Device.ManagementServer.ConnectionRequestUsername', type: 'string', writable: true },
  { path: 'Device.ManagementServer.ConnectionRequestPassword', type: 'string', writable: true },
  { path: 'Device.ManagementServer.Username', type: 'string', writable: true },
  { path: 'Device.ManagementServer.Password', type: 'string', writable: true },
  { path: 'Device.IP.Interface.3.IPv4Address.1.Address', type: 'string', writable: false },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ConnectionStatus',
    type: 'string',
    writable: false,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.MACAddress',
    type: 'string',
    writable: false,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.ConnectionStatus',
    type: 'string',
    writable: false,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Username',
    type: 'string',
    writable: true,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Password',
    type: 'string',
    writable: true,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Uptime',
    type: 'unsignedInt',
    writable: false,
  },
  {
    path: 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.LastActivity',
    type: 'unsignedInt',
    writable: false,
  },
  {
    path: 'Device.WANDevice.1.WANCommonInterfaceConfig.LayerDownPWENegotiationState',
    type: 'string',
    writable: false,
  },
  // TR-181 spells PPPoE creds on the PPP interface, not on the WAN connection object
  // (survey `03` §۱.۳, the 'PPPoE creds' row: `Device.PPP.Interface.{i}.Username/Password/
  // Enable/ConnectionTrigger`). It is the **same value** as the `WANPPPConnection.1.Username` row
  // above, which is the problem consolidate C5 names: one credential with two write targets is two
  // sources of truth, and the console cannot say which one a write lands in. Until M1's manifest
  // names the single write target for `wan.pppoe.username`, this row is a read-only mirror — the
  // leaf is real (TR-098 has no PPP interface at all, TR-181 reads it there), only the `writable`
  // claim is withheld.
  { path: 'Device.PPP.Interface.1.Username', type: 'string', writable: false },
  { path: 'Device.Ethernet.Interface.1.Status', type: 'string', writable: false },
  { path: 'Device.Ethernet.Interface.1.MaxBitRate', type: 'unsignedInt', writable: true },
  {
    path: 'Device.LANDevice.1.LANHostConfigManagement.IPAddressRange',
    type: 'string',
    writable: true,
  },
  { path: 'Device.LANDevice.1.Hosts.HostNumber', type: 'unsignedInt', writable: false },
];

const TR181_WIFI: readonly ParamSpec[] = [
  // The primary SSID is a real leaf on this device (TR-181 spells it per SSID instance; the
  // instance number is per model, which is why it is declared here rather than computed).
  { path: 'Device.WiFi.SSID.1.SSID', type: 'string', writable: true },
  { path: 'Device.WiFi.SSID.2.SSID', type: 'string', writable: true },
  { path: 'Device.WiFi.SSID.1.Enable', type: 'boolean', writable: true },
  { path: 'Device.WiFi.AccessPoint.1.Security.Mode', type: 'string', writable: true },
  { path: 'Device.WiFi.AccessPoint.1.Security.KeyPassed', type: 'string', writable: true },
  {
    path: 'Device.WiFi.AccessPoint.1.AssociatedDeviceNumber',
    type: 'unsignedInt',
    writable: false,
  },
  // KNOWN DEBT, registered by `test/noInventedPaths.test.ts`: survey `03` records CT-COM
  // `TxPower` only as an *optical* read (`X_CT-COM_GponInterfaceConfig`, §۴.۵) and Wi-Fi power as
  // the standard `TransmitPower` on `WLANConfiguration.{n}` (§۲.۴), so this leaf is unwitnessed.
  // It is outside this brief's scope (six invented virtual params, not this one) and its owner is
  // M1's catalog — a binding nobody witnessed must not be renderable — with M5's discovery to settle it.
  { path: 'Device.WiFi.Radio.1.X_CT-COM_TxPower', type: 'string', writable: true },
];

/**
 * TR-098 (`InternetGatewayDevice.`) leaves, declared as TR-098 spells them.
 *
 * Only the subset the evidence carries — survey `03` §۱.۳ (the verified TR-098/TR-181 pairs and
 * the §۱.۳ spec-fact paragraph on which `DeviceInfo.*` leaves are read-only), §۲.۱ (Huawei
 * `ManagementServer.{URL,PeriodicInformInterval}` and `Time.NTPServer1`, read-write in the field),
 * §۲.۴ (a real `WLANConfiguration.{n}` attribute dump: `SSID`, `Enable`, `Channel`), and §۳.۱
 * (ZTE on TR-098: `WANDevice.1.WANConnectionDevice.{d}.WANPPPConnection.{i}.Username`).
 *
 * The TR-181 shape is deliberately NOT copied in: `ManufacturerOUI` where TR-181 says `OUI`,
 * `Channel` on the WLAN configuration where TR-181 keeps it on the radio object, PPPoE creds on
 * the WAN PPP connection where TR-181 has a `Device.PPP.Interface`. Optical power appears nowhere:
 * TR-098 names no standard leaf for it, which is precisely why the fleet reads it off the OLT
 * (`ACS_MULTI_VENDOR_FA.md` §۹ rule O-1).
 */
const TR098_COMMON: readonly ParamSpec[] = [
  { path: 'InternetGatewayDevice.DeviceInfo.Manufacturer', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.ManufacturerOUI', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.ProductClass', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.SerialNumber', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.SoftwareVersion', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.HardwareVersion', type: 'string', writable: false },
  { path: 'InternetGatewayDevice.DeviceInfo.UpTime', type: 'unsignedInt', writable: false },
  { path: 'InternetGatewayDevice.ManagementServer.URL', type: 'string', writable: true },
  {
    path: 'InternetGatewayDevice.ManagementServer.PeriodicInformInterval',
    type: 'unsignedInt',
    writable: true,
  },
  {
    path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID',
    type: 'string',
    writable: true,
  },
  {
    path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.Enable',
    type: 'boolean',
    writable: true,
  },
  {
    path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.Channel',
    type: 'int',
    writable: true,
  },
  {
    path: 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.Username',
    type: 'string',
    writable: true,
  },
  {
    path: 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.NATEnabled',
    type: 'boolean',
    writable: true,
  },
];

/**
 * Which root a model answers, as the fixture's stand-in for the read a real ACS performs
 * (rule M0-2: the root comes off the device, never off the brand — one family ships both trees,
 * ZTE F660 on TR-098 and the ZTE H-series on TR-181, survey `03` §۳). A model not listed here
 * answers the TR-181 table; nothing is decided by swapping a prefix.
 */
const TR098_MODELS: readonly string[] = ['F670v8'];

/**
 * Rule M0-2, exposed: the root a device answers on, read off the device. The diagnostics read
 * (`api/fixtureApi.ts`) needs the same fact the parameter table needs, and a second copy of the
 * model list would be a second answer to the same question.
 */
export function dataModelRootFor(device: FixtureDevice): 'tr098' | 'tr181' {
  return TR098_MODELS.includes(device.product_class) ? 'tr098' : 'tr181';
}

function parameterSpecsFor(device: FixtureDevice): readonly ParamSpec[] {
  return dataModelRootFor(device) === 'tr098' ? TR098_COMMON : [...TR181_COMMON, ...TR181_WIFI];
}

function parameterValue(
  device: FixtureDevice,
  spec: { path: string; type: string },
  r: Rand,
): unknown {
  const p = spec.path;
  switch (p) {
    case 'Device.DeviceInfo.Manufacturer':
      return device.manufacturer;
    case 'Device.DeviceInfo.OUI':
      return device.oui;
    case 'Device.DeviceInfo.ProductClass':
      return device.product_class;
    case 'Device.DeviceInfo.SerialNumber':
      return device.serial;
    case 'Device.DeviceInfo.SoftwareVersion':
      return device.software_version;
    case 'Device.DeviceInfo.HardwareVersion':
      return 'H/W 1.4';
    case 'Device.DeviceInfo.UpTime':
      return device.online ? ri(r, 3_600, 5_184_000) : 0;
    case 'Device.DeviceInfo.FirstUseDate':
      return `2024-0${ri(r, 1, 9)}-1${ri(r, 0, 8)}T07:30:00Z`;
    case 'Device.DeviceInfo.ProcessStatus.Status':
      return device.online ? 'Normal' : 'Shutdown';
    case 'Device.Time.Status':
      return device.online ? 'Synchronized' : 'NotSynchronized';
    case 'Device.Time.NTPServer1':
      return 'ntp.asre.ac.ir';
    case 'Device.ManagementServer.URL':
      return 'https://acs.example:7501/';
    case 'Device.ManagementServer.PeriodicInformInterval':
      return rpick(r, [180, 300, 900]);
    case 'Device.ManagementServer.ConnectionRequestUsername':
      return 'csr_user';
    case 'Device.ManagementServer.ConnectionRequestPassword':
      return MASKED;
    case 'Device.ManagementServer.Username':
      return 'acs_user';
    case 'Device.ManagementServer.Password':
      return MASKED;
    case 'Device.IP.Interface.3.IPv4Address.1.Address':
      return device.wanIp;
    case 'Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ConnectionStatus':
      return device.online ? 'Up' : 'Down';
    case 'Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.MACAddress':
      return macOf(device.serial);
    case 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.ConnectionStatus':
      return device.online && device.pppoeUser ? 'Up' : 'Down';
    case 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Username':
      return device.pppoeUser || 'unset';
    case 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Password':
      return MASKED;
    case 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Uptime':
      return device.online ? ri(r, 600, 2_592_000) : 0;
    case 'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.LastActivity':
      return ri(r, 0, 300);
    case 'Device.WANDevice.1.WANCommonInterfaceConfig.LayerDownPWENegotiationState':
      return device.online ? 'Session_Established' : 'PLOAM_Aborted';
    // The subscriber's PPPoE user, on the leaf TR-181 actually spells it on (survey `03` §۱.۳
    // 'PPPoE creds'). It used to be published a second time under an invented vendor subtree.
    case 'Device.PPP.Interface.1.Username':
      return device.pppoeUser || 'unset';
    case 'Device.Ethernet.Interface.1.Status':
      return device.online ? 'Up' : 'Down';
    case 'Device.Ethernet.Interface.1.MaxBitRate':
      return 1000;
    case 'Device.LANDevice.1.LANHostConfigManagement.IPAddressRange':
      return `${device.lanIp.slice(0, device.lanIp.lastIndexOf('.'))}.2-${device.lanIp.slice(0, device.lanIp.lastIndexOf('.'))}.254`;
    case 'Device.LANDevice.1.Hosts.HostNumber':
      return ri(r, 1, 9);
    case 'Device.WiFi.SSID.1.SSID':
      return device.wifiSsid;
    case 'Device.WiFi.SSID.2.SSID':
      return `${device.wifiSsid}_IoT`;
    case 'Device.WiFi.SSID.1.Enable':
      return true;
    case 'Device.WiFi.AccessPoint.1.Security.Mode':
      return 'WPA2-Personal';
    case 'Device.WiFi.AccessPoint.1.Security.KeyPassed':
      return MASKED;
    case 'Device.WiFi.AccessPoint.1.AssociatedDeviceNumber':
      return stationCount(device);
    case 'Device.WiFi.Radio.1.X_CT-COM_TxPower':
      return '100';
    // The TR-098 table, arm by arm. The apparent duplication with the `Device.` cases above is the
    // point, not an accident: two roots spell the same concepts differently and one root's answer
    // is never built from the other (`provisioning/slots.py` declares every slot per root too).
    // Optical Rx/Tx are absent by design — there is no witnessed TR-098 leaf for them and the
    // fleet reads them off the OLT (`ACS_MULTI_VENDOR_FA.md` §۹ rule O-1) — and so is any
    // "service state": that is a BSS fact of `nas_ref`, shown by `IdentityPanel`, not a TR-069
    // parameter (gap `G-PRV-01`; §۲.۲ makes such concepts ACS-internal, never a device path).
    case 'InternetGatewayDevice.DeviceInfo.Manufacturer':
      return device.manufacturer;
    case 'InternetGatewayDevice.DeviceInfo.ManufacturerOUI':
      return device.oui;
    case 'InternetGatewayDevice.DeviceInfo.ProductClass':
      return device.product_class;
    case 'InternetGatewayDevice.DeviceInfo.SerialNumber':
      return device.serial;
    case 'InternetGatewayDevice.DeviceInfo.SoftwareVersion':
      return device.software_version;
    case 'InternetGatewayDevice.DeviceInfo.HardwareVersion':
      return 'H/W 2.1';
    case 'InternetGatewayDevice.DeviceInfo.UpTime':
      return device.online ? ri(r, 3_600, 5_184_000) : 0;
    case 'InternetGatewayDevice.ManagementServer.URL':
      return 'https://acs.example:7501/';
    case 'InternetGatewayDevice.ManagementServer.PeriodicInformInterval':
      return rpick(r, [300, 900]);
    case 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID':
      return device.wifiSsid;
    case 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.Enable':
      // The same convention as the TR-181 `Device.WiFi.SSID.1.Enable` arm: the SSID is on. A
      // device being offline says nothing about its radio being enabled, so it is not a value here.
      return true;
    case 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.Channel':
      return rpick(r, [1, 6, 11]);
    case 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.Username':
      return device.pppoeUser || 'unset';
    case 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.NATEnabled':
      return true;
    default:
      return null;
  }
}

export function macOf(serial: string): string {
  const clean = serial
    .replace(/[^A-Fa-f0-9]/g, '')
    .toUpperCase()
    .padEnd(12, '0');
  const bytes: string[] = [];
  for (let i = 0; i < 6; i += 1) {
    bytes.push(clean.slice(i * 2, i * 2 + 2));
  }
  return bytes.join(':');
}

export function stationCount(device: FixtureDevice): number {
  if (!device.online) return 0;
  const seed = Number.parseInt(macOf(device.serial).replace(/:/g, '').slice(0, 3), 16);
  return (seed % 6) + 1;
}

/** The snapshot table for one device (sorted by path, like the NBI returns). */
/** Cached so a snapshot never changes between renders or pages. */
const PARAM_CACHE = new Map<string, ParameterItem[]>();

export function parametersFor(device: FixtureDevice): ParameterItem[] {
  const cached = PARAM_CACHE.get(device.acs_device_id);
  if (cached) return cached;
  const r = mulberry32(hashString(device.serial));
  // The device answers ONE declared table — the one for the root it speaks.
  const specs = parameterSpecsFor(device);
  const items = specs
    .map((spec) => ({
      path: spec.path,
      value: parameterValue(device, spec, r),
      type: spec.type,
      writable: spec.writable,
      captured_at: device.last_inform_at,
    }))
    .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  PARAM_CACHE.set(device.acs_device_id, items);
  return items;
}
